import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useInteractions, useClick, useDismiss, useHover, arrow as arrowMiddleware, FloatingArrow, autoUpdate, useFocus, hide, shift, useClientPoint, } from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { useControlledState } from '@/js/libs/hooks/useControlledState/index.ts';
import { useMergeRefs } from '@/js/libs/hooks/useMergeRefs/index.ts';
import { getFloatingPlacement } from './getFloatingPlacement.js';
import { Content, Trigger, Wrapper } from './styled.js';
import { useExtendedClick } from './useClickExtended.js';
export const Tooltip = memo(forwardRef(function Tooltip({ children, className, tooltip, renderTooltipWithoutWrapper = false, WrapperComponent = Wrapper, disabled, visible, closeOnTriggerHidden, dataTestId, containerComponentProps, trigger = 'hover', zIndex, placement = 'auto', onVisibleChange, ContainerComponent = Trigger, type = 'tooltip', autoSize = false, contentPadding = true, delayShow = 100, delayHide = 100, arrow = true, backgroundColor, anchorToCursor = false, }, ref) {
    const [isVisible, setIsVisible] = useControlledState(visible, false, onVisibleChange);
    const triggers = useMemo(() => {
        let triggersList = Array.isArray(trigger) ? trigger : [trigger];
        if (typeof window !== 'undefined') {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            if (isTouchDevice) {
                // Not all touch devices can't do right click, so replace it with click
                triggersList = triggersList.reduce((acc, t) => {
                    acc.push(t === 'right-click' ? 'click' : t);
                    return acc;
                }, new Array());
            }
        }
        return triggersList;
    }, [trigger]);
    const theme = useTheme();
    const [defaultPlacement, placementMiddleware] = getFloatingPlacement(placement, !anchorToCursor);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context, middlewareData } = useFloating({
        placement: defaultPlacement,
        open: isVisible,
        onOpenChange: setIsVisible,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            placementMiddleware,
            // A cursor-anchored tooltip can be opened right next to the viewport edge.
            anchorToCursor && shift({ padding: 8 }),
            // eslint-disable-next-line react-hooks/refs
            arrowMiddleware({
                element: arrowRef,
                padding: theme.borders.radiusMedium,
            }),
            closeOnTriggerHidden && hide(),
        ],
    });
    const containerRef = useMergeRefs(refs.setReference, ref);
    // This property added by hide middleware.
    // https://floating-ui.com/docs/hide#referencehidden
    const refHidden = middlewareData.hide?.referenceHidden;
    useEffect(() => {
        if (refHidden) {
            setIsVisible(false);
        }
    }, [refHidden, setIsVisible]);
    const leftClick = useClick(context, {
        event: 'click',
        enabled: triggers.includes('click'),
    });
    const rightClick = useExtendedClick(context, {
        event: 'right-click',
        enabled: triggers.includes('right-click'),
    });
    const hover = useHover(context, {
        delay: {
            open: delayShow,
            close: delayHide,
        },
        enabled: triggers.includes('hover'),
    });
    const focus = useFocus(context, {
        enabled: triggers.includes('focus'),
    });
    // Explicit `x` keeps the tooltip pinned to the entry point instead of following the cursor.
    const [cursorX, setCursorX] = useState(null);
    const clientPoint = useClientPoint(context, {
        enabled: anchorToCursor,
        axis: 'x',
        x: cursorX,
    });
    const handleTriggerMouseEnter = useCallbackRef((event) => {
        if (!isVisible) {
            setCursorX(event.clientX);
        }
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([leftClick, hover, rightClick, focus, clientPoint]);
    useDismiss(context, {
        enabled: isVisible,
    });
    return disabled || !tooltip ? (_jsx(ContainerComponent, { className: className, ref: ref, "data-testid": dataTestId, ...containerComponentProps, children: children })) : (_jsxs(_Fragment, { children: [isVisible &&
                ReactDOM.createPortal(_jsxs(WrapperComponent, { $zIndex: zIndex, ref: refs.setFloating, ...getFloatingProps(), style: {
                        ...floatingStyles,
                        ...(backgroundColor ? { background: backgroundColor } : {}),
                    }, type: type, "data-testid": 'tooltip-wrapper', children: [renderTooltipWithoutWrapper ? (tooltip) : (_jsx(Content, { $contentPadding: contentPadding, $autoSize: autoSize, children: tooltip })), arrow && (_jsx(FloatingArrow, { ref: arrowRef, context: context, fill: backgroundColor ? backgroundColor : type === 'tooltip' ? theme.colors.black_1 : theme.colors.grey00_1, width: 11, height: 5 }))] }), document.body), _jsx(ContainerComponent, { "data-testid": dataTestId, ...containerComponentProps, className: className, ref: containerRef, ...getReferenceProps(anchorToCursor ? { onMouseEnter: handleTriggerMouseEnter } : undefined), children: children })] }));
}));
// https://www.figma.com/file/OPrQYe21YjiU2kmoGd571W/AdConsole---Design-System?node-id=648%3A2778
export const DelayedTooltip = (props) => _jsx(Tooltip, { ...props, delayShow: 300, delayHide: 500 });
